/**
 * @fileoverview Table cell wrapper component for companies table
 * @active CONFIRMED - This component is actively used in the application
 *
 * This component wraps table cells (Td) with consistent styling and behavior,
 * handling conditional rendering based on column visibility and applying
 * appropriate styling based on filters and sorting.
 *
 * @see {@link src/components/companies/companiesList/newCompaniesList/companiesTable/companyInfoTable/CompaniesInfoTableRow.tsx} - Parent row component
 * @see {@link src/components/companies/companiesList/newCompaniesList/companiesTable/companyInfoTable/CompanyNameRow.tsx} - Company name column component
 * @see {@link src/components/companies/companiesList/newCompaniesList/companiesTable/context/TableContext.tsx} - Table context for column widths
 * @see {@link src/store/newView/newView.selectors.ts} - Selectors for view state
 * @see {@link src/components/companies/companiesList/companiesListComponents/tableMain/TableElements.jsx} - Legacy implementation (deprecated)
 */

// src/components/companies/companiesList/newCompaniesList/companiesTable/shared/TdWrapper.tsx

"use client";

import { memo, useContext } from "react";
import type { ReactNode } from "react";

import { Flex, Td } from "@chakra-ui/react";
import { type FlexProps } from "@chakra-ui/react";
import { useSelector } from "react-redux";

import newViewSelectors from "@/store/newView/newView.selectors";

import { listFields } from "../../data/lists";
import TableContext from "../context/TableContext";

interface TdWrapperProps {
  children: ReactNode;
  style?: FlexProps;
  isLast: boolean;
  id: string;
  idx: number;
}

const TdWrapper = ({ children, style, isLast, id, idx }: TdWrapperProps) => {
  const currentView = useSelector(newViewSelectors.getCurrentView());
  const viewParams = useSelector(newViewSelectors.getViewParams());
  const currentParams = viewParams?.[currentView?.id];
  const { columnsWidth } = useContext(TableContext);

  const allFiltersArr = currentParams?.arrayFilters
    ? Object?.keys(currentParams?.arrayFilters)
    : [];
  const convertFiltersArr = listFields
    ?.filter((item) => allFiltersArr.includes(item.filterField))
    ?.map((f) => f?.id);

  const tagsIndex = [4, 5, 6, 8, 14, 16, 17, 18];

  // Special handling for company column (idx 0)
  // The company column can be identified by either id === "company" || idx === 0
  const isCompanyColumn = id === "company" || idx === 0;

  // Enhanced debugging for the company column issue
  console.log(
    `TdWrapper - id: ${id}, idx: ${idx}, isCompanyColumn: ${isCompanyColumn}`
  );
  if (isCompanyColumn) {
    console.log(
      `TdWrapper - Company Column- id: ${id}, idx: ${idx}, applying sticky positioning`
    );
  }

  return (
    <>
      {currentParams?.columns?.includes(id) ? (
        <Td
          p={"0"}
          bg={
            convertFiltersArr?.includes(id)
              ? "lightGreen"
              : currentParams?.orderBy?.id === id
                ? "lightYellow"
                : "transtparent"
          }
          // Apply sticky positioning only to company column
          {...(isCompanyColumn && {
            pos: "sticky",
            left: "0",
            zIndex: "10",
            bg: "white",
            "data-company-column": "true",
          })}
        >
          <Flex
            borderRightWidth={isCompanyColumn ? "0" : isLast ? "0" : "1px"}
            borderColor={"lightGray"}
            _groupHover={{ bg: "lightBlue2" }}
            px={"10px"}
            py={"4px"}
            h={"32px"}
            align={"center"}
            justify={"center"}
            overflow={"hidden"}
            minW={"60px"}
            // Ensure consistent width for all cells
            w={
              !tagsIndex?.includes(idx) && columnsWidth && columnsWidth[idx]
                ? `${columnsWidth[idx]}px`
                : "auto"
            }
            // Apply background color to ensure content is visible when sticky
            bg={
              isCompanyColumn
                ? convertFiltersArr?.includes(id)
                  ? "lightGreen"
                  : currentParams?.orderBy?.id === id
                    ? "lightYellow"
                    : "white"
                : "transparent"
            }
            {...style}
          >
            {children}
          </Flex>
        </Td>
      ) : null}
    </>
  );
};

export default memo(TdWrapper);
